import React from 'react';

import { cn } from '@epoch-ui/lib/utils';
import { Input } from '@epoch-ui/components/Input';
import { SelectInput, SelectContent, SelectItem } from '@epoch-ui/components/Select';
import {
  FormFieldSet,
  FormField,
  FormLabel,
  CompositeFormMessage,
  FormControl,
  FormItem
} from '@epoch-ui/components/Form';
import { TimeOffsetUnit, TimeOffsetRelation } from 'constants/enums';

type TimeOffsetValue = {
  quantity: number | null;
  unit: TimeOffsetUnit;
  relation: TimeOffsetRelation;
};

type TimeOffsetOption = {
  value: TimeOffsetUnit | TimeOffsetRelation | (string & {});
  label: React.ReactNode;
};

type TimeOffsetConfig = {
  units: TimeOffsetOption[];
  relations: TimeOffsetOption[];
};

interface TimeOffsetFieldProps
  extends React.ComponentProps<typeof FormFieldSet>,
    Pick<React.ComponentProps<typeof FormField>, 'name' | 'disabled'> {
  label?: React.ReactNode;
  config: TimeOffsetConfig;
}

const TimeOffsetField = React.forwardRef<HTMLFieldSetElement, TimeOffsetFieldProps>(
  ({ name, disabled, config, label, ...props }, ref) => {
    const defaultValue: TimeOffsetValue = React.useMemo(
      () => ({
        quantity: 1,
        unit: (config.units[0]?.value as TimeOffsetUnit) ?? TimeOffsetUnit.MINUTES,
        relation: (config.relations[0]?.value as TimeOffsetRelation) ?? TimeOffsetRelation.BEFORE
      }),
      [config.units, config.relations]
    );

    return (
      <FormFieldSet ref={ref} {...props}>
        <FormField
          name={name}
          disabled={disabled}
          render={({ field, fieldState }) => {
            const fieldValue = field.value ?? defaultValue;

            const isRootError = !!fieldState.error?.message;

            const updateValue = (next: Partial<TimeOffsetValue>): void => {
              field.onChange({
                ...fieldValue,
                ...next
              });
            };

            const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
              const quantity = Number(e.target.value);

              updateValue({
                quantity: Number.isNaN(quantity) || quantity <= 0 ? null : quantity
              });
            };

            return (
              <FormItem>
                {label && (
                  <FormLabel className={cn('tw-mb-3', disabled && 'tw-opacity-40')}>
                    {label}
                  </FormLabel>
                )}

                <div className="tw-flex tw-items-center tw-gap-3">
                  <FormControl className="tw-mt-0 tw-w-1/3">
                    <Input
                      type="number"
                      min={1}
                      value={fieldValue.quantity ?? ''}
                      disabled={disabled}
                      error={isRootError || !!(fieldState.error as any)?.quantity?.message}
                      onBlur={field.onBlur}
                      onChange={handleQuantityChange}
                    />
                  </FormControl>

                  <FormControl className="tw-mt-0" id="time-offset-field-unit">
                    <SelectInput
                      value={fieldValue.unit}
                      disabled={disabled}
                      error={isRootError || !!(fieldState.error as any)?.unit?.message}
                      onValueChange={(value: string) => {
                        updateValue({ unit: value as TimeOffsetUnit });
                      }}
                    >
                      <SelectContent>
                        {config.units.map(({ value, label: unitLabel }) => (
                          <SelectItem key={value} value={value}>
                            {unitLabel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </SelectInput>
                  </FormControl>
                </div>

                <FormControl id="time-offset-field-relation">
                  <SelectInput
                    value={fieldValue.relation}
                    disabled={disabled}
                    error={isRootError || !!(fieldState.error as any)?.relation?.message}
                    onValueChange={(value: string) => {
                      updateValue({ relation: value as TimeOffsetRelation });
                    }}
                  >
                    <SelectContent>
                      {config.relations.map(({ value, label: relationLabel }) => (
                        <SelectItem key={value} value={value}>
                          {relationLabel}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </SelectInput>
                </FormControl>

                <CompositeFormMessage priority={['quantity', 'unit', 'relation']} />
              </FormItem>
            );
          }}
        />
      </FormFieldSet>
    );
  }
);

export { TimeOffsetField, type TimeOffsetConfig };
